import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, eur, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import WaiterCalls from "@/components/dashboard/WaiterCalls";
import {
  Play, Square, AlertTriangle, Users, ClipboardList, ChefHat, Bell, Timer,
  HandPlatter, Wallet, ClipboardList as ListIcon, LayoutGrid, CreditCard, ChefHat as KdsIcon, ArrowRight,
  ChevronDown, X, ScrollText, Printer,
} from "lucide-react";

const fmtHM = (iso) => { try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const SummaryLine = ({ k, v, strong }) => (
  <div className="flex justify-between border-b border-[#132420] py-1.5"><span className="text-stone-400">{k}</span><span className={strong ? "font-bold text-[#E7C97A]" : "text-stone-100"}>{v}</span></div>
);
const fmtDur = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${String(m % 60).padStart(2, "0")}`;
};

function Counter({ testid, icon: Icon, label, value, accent, urgent, onClick, active }) {
  return (
    <div data-testid={testid} onClick={onClick} role="button"
      className={`rounded-2xl bg-[#111F1A] border p-4 cursor-pointer transition-all hover:border-[#C69C4A]/60 hover:bg-[#132420] active:scale-[0.99] ${active ? "border-[#C69C4A] ring-1 ring-[#C69C4A]/40" : (urgent ? "border-red-500/50" : "border-[#1B3028]")}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg grid place-items-center" style={{ backgroundColor: `${accent}1A` }}><Icon className="w-4 h-4" style={{ color: accent }} /></div>
        <span className="text-[11px] uppercase tracking-wider text-stone-500">{label}</span>
      </div>
      <p className="font-bold text-3xl" style={{ color: urgent && value > 0 ? "#F87171" : "#fff" }}>{value}</p>
      <p className="mt-1 text-[10px] font-semibold text-[#C69C4A] inline-flex items-center gap-1">{active ? "Masquer" : "Voir le détail"} <ChevronDown className={`w-3 h-3 transition-transform ${active ? "rotate-180" : ""}`} /></p>
    </div>
  );
}

export default function Cockpit({ slug, onNavigate, requests = [], onClaim, onHandle }) {
  const [stats, setStats] = useState(null);
  const [svc, setSvc] = useState(null);
  const [closeWarn, setCloseWarn] = useState(null); // warnings object when confirmation needed
  const [openMetric, setOpenMetric] = useState(null);
  const [detail, setDetail] = useState({ loading: false, data: null });
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summary, setSummary] = useState(null);

  const openSummary = async () => {
    setSummaryOpen(true); setSummary(null);
    try { const d = await api.serviceSummary(slug); setSummary(d); }
    catch (e) { toast.error("Aucun service à résumer"); setSummaryOpen(false); }
  };

  const load = useCallback(() => {
    if (!slug) return;
    api.getStats(slug, { range: "today" }).then(setStats).catch(() => {});
    api.serviceCurrent(slug).then(setSvc).catch(() => {});
  }, [slug]);

  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, [load]);

  const openPanel = (metric) => setOpenMetric((m) => (m === metric ? null : metric));

  // Fetch + keep the open panel live (auto-refresh, no manual reload).
  const live = stats?.live || {};
  const liveSig = `${live.active_tables}|${live.orders_new}|${live.orders_preparing}|${live.orders_ready}|${live.orders_late}|${live.waiter_requests_pending}|${live.tables_awaiting_payment}`;
  useEffect(() => {
    if (!openMetric || !slug) { setDetail({ loading: false, data: null }); return; }
    let alive = true;
    const fetchDetail = () => {
      api.cockpitDetail(slug, openMetric)
        .then((d) => { if (alive) setDetail({ loading: false, data: d }); })
        .catch(() => { if (alive) setDetail({ loading: false, data: null }); });
    };
    setDetail({ loading: true, data: null });
    fetchDetail();
    const id = setInterval(fetchDetail, 4000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line
  }, [openMetric, slug, liveSig]);

  const startService = async () => {
    try { await api.serviceStart(slug); toast.success("Service démarré"); load(); }
    catch (e) { toast.error("Erreur au démarrage du service"); }
  };
  const closeService = async (force = false) => {
    try {
      await api.serviceClose(slug, force);
      toast.success("Service clôturé");
      setCloseWarn(null); load();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 409 && det?.warnings) { setCloseWarn(det.warnings); }
      else toast.error("Erreur à la clôture du service");
    }
  };

  const svcRevenue = stats?.kpis?.revenue?.value; // today revenue (service-scoped available via range=service if needed)
  const isOpen = svc?.status === "OPEN";
  const overdue = svc?.overdue;

  const QUICK = [
    { label: "Voir les commandes", icon: ListIcon, tab: "orders" },
    { label: "Voir les tables", icon: LayoutGrid, tab: "tables" },
    { label: "Encaisser", icon: CreditCard, tab: "payments" },
    { label: "Mode cuisine", icon: KdsIcon, href: "/kds" },
  ];

  return (
    <div className="space-y-6">
      {/* Service control */}
      <div data-testid="service-control" className={`rounded-3xl border p-5 ${isOpen ? (overdue ? "bg-[#1C1210] border-amber-500/50" : "bg-[#0F1B16] border-[#1B3028]") : "bg-[#111F1A] border-[#1B3028]"}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-[#C69C4A] font-bold">Service en cours</p>
            {isOpen ? (
              <p className="font-serif-display text-2xl text-white mt-1">Ouvert depuis {svc.hours_open != null ? `${svc.hours_open} h` : "—"} <span className="text-sm text-stone-400">· par {svc.service?.started_by || "—"}</span></p>
            ) : (
              <p className="font-serif-display text-2xl text-stone-300 mt-1">Aucun service ouvert</p>
            )}
            {overdue && <p className="text-sm text-amber-300 mt-1 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Service ouvert depuis longtemps — pensez à le clôturer</p>}
          </div>
          {isOpen ? (
            <Button data-testid="service-close-btn" onClick={() => closeService(false)} className="rounded-full bg-red-500 hover:bg-red-600 text-white font-bold h-12 px-6"><Square className="w-4 h-4 mr-2" /> Clôturer le service</Button>
          ) : (
            <Button data-testid="service-start-btn" onClick={startService} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 px-6"><Play className="w-4 h-4 mr-2" /> Démarrer le service</Button>
          )}
          <Button data-testid="service-summary-btn" onClick={openSummary} variant="outline" className="rounded-full bg-transparent border-[#1B3028] text-stone-200 hover:bg-[#111F1A] h-12 px-5"><ScrollText className="w-4 h-4 mr-2" /> Résumé du service</Button>
        </div>
      </div>

      {/* Live counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Counter testid="cockpit-tables" icon={Users} label="Tables actives" value={live.active_tables || 0} accent="#34D399" onClick={() => openPanel("active_tables")} active={openMetric === "active_tables"} />
        <Counter testid="cockpit-new" icon={ClipboardList} label="Nouvelles" value={live.orders_new ?? 0} accent="#C69C4A" onClick={() => openPanel("orders_new")} active={openMetric === "orders_new"} />
        <Counter testid="cockpit-preparing" icon={ChefHat} label="En préparation" value={live.orders_preparing || 0} accent="#60A5FA" onClick={() => openPanel("orders_preparing")} active={openMetric === "orders_preparing"} />
        <Counter testid="cockpit-ready" icon={Bell} label="Prêtes" value={live.orders_ready ?? 0} accent="#34D399" onClick={() => openPanel("orders_ready")} active={openMetric === "orders_ready"} />
        <Counter testid="cockpit-late" icon={Timer} label="En retard" value={live.orders_late || 0} accent="#F87171" urgent onClick={() => openPanel("orders_late")} active={openMetric === "orders_late"} />
        <Counter testid="cockpit-calls" icon={HandPlatter} label="Appels serveur" value={live.waiter_requests_pending || 0} accent="#F87171" urgent onClick={() => openPanel("waiter_requests")} active={openMetric === "waiter_requests"} />
        <Counter testid="cockpit-topay" icon={CreditCard} label="À encaisser" value={live.tables_awaiting_payment || 0} accent="#E7C97A" onClick={() => openPanel("pending_payments")} active={openMetric === "pending_payments"} />
        <Counter testid="cockpit-revenue" icon={Wallet} label="CA du jour" value={eur(svcRevenue || 0)} accent="#C69C4A" onClick={() => openPanel("revenue")} active={openMetric === "revenue"} />
      </div>

      {/* Inline drill-down panel (one at a time) — real data, auto-refreshed */}
      {openMetric && (
        <CockpitDetail metric={openMetric} detail={detail} onClose={() => setOpenMetric(null)} onNavigate={onNavigate} caRevenue={svcRevenue || 0} />
      )}

      {/* Stale sessions guardrail */}
      {live.stale_session_count > 0 && (
        <div data-testid="cockpit-stale" className="rounded-2xl bg-[#1C1210] border border-amber-500/40 p-4">
          <p className="text-sm font-semibold text-amber-300 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {live.stale_session_count} table(s) ouvertes depuis longtemps à vérifier</p>
          <p className="text-xs text-stone-400 mt-1">Tables {live.stale_sessions?.map((s) => s.table_number).join(", ")} — inactives depuis plusieurs heures. Clôturez-les dans l'onglet Tables si nécessaire.</p>
        </div>
      )}

      {/* Quick access */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {QUICK.map((q) => (
          <button key={q.label} data-testid={`cockpit-quick-${q.tab || "kds"}`}
            onClick={() => q.href ? window.open(q.href, "_blank") : onNavigate(q.tab)}
            className="group rounded-2xl bg-[#1A3D32] hover:bg-[#123026] border border-[#2C5446] p-4 text-left transition-colors">
            <q.icon className="w-5 h-5 text-[#C69C4A] mb-2" />
            <p className="font-semibold text-stone-100 text-sm flex items-center gap-1">{q.label} <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" /></p>
          </button>
        ))}
      </div>

      {/* Waiter calls */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider text-stone-300 mb-3">Appels serveur</h3>
        <WaiterCalls requests={requests} onClaim={onClaim} onHandle={onHandle} />
      </div>

      {/* Close confirmation with active-operations warnings (never silent) */}
      <Dialog open={summaryOpen} onOpenChange={(o) => !o && setSummaryOpen(false)}>
        <DialogContent data-testid="service-summary-dialog" className="max-w-md rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl flex items-center gap-2"><ScrollText className="w-5 h-5 text-[#C69C4A]" /> Résumé du service</DialogTitle>
            <DialogDescription className="text-stone-400 text-xs">{summary ? (summary.window.closed ? "Service clôturé" : "Service en cours") : "Chargement…"}</DialogDescription>
          </DialogHeader>
          {summary && (
            <div id="service-summary-print" className="text-sm">
              <SummaryLine k="Période" v={`${fmtHM(summary.window.start)} → ${summary.window.closed ? fmtHM(summary.window.end) : "en cours"}`} />
              <SummaryLine k="CA du service" v={eur(summary.revenue)} strong />
              <SummaryLine k="Couverts" v={summary.covers.complete ? summary.covers.total : `${summary.covers.total} · incomplet (${summary.covers.tables_without_covers} table(s) sans couverts)`} />
              <SummaryLine k="Ticket moyen / couvert" v={summary.avg_ticket_per_cover != null ? eur(summary.avg_ticket_per_cover) : "— (couverts non renseignés)"} />
              <SummaryLine k="Tables servies" v={summary.tables_served} />
              <SummaryLine k="Commandes" v={summary.order_count} />
              <SummaryLine k="Sessions QR (info)" v={summary.qr_sessions} />
              <SummaryLine k="Upsells acceptés" v={`${summary.upsells.count} · ${eur(summary.upsells.value)}`} />
              <SummaryLine k="Impayés (clôtures forcées)" v={eur(summary.unpaid_amount)} />
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button data-testid="service-summary-print-btn" onClick={() => window.print()} disabled={!summary} variant="outline" className="rounded-full bg-transparent border-[#1B3028] text-stone-200"><Printer className="w-4 h-4 mr-2" /> Imprimer</Button>
            <Button data-testid="service-summary-pdf-btn" onClick={async () => { try { const b = await api.serviceSummaryPdf(slug, summary.service?.id); downloadBlob(b, "resume-service.pdf"); } catch { toast.error("Export PDF impossible"); } }} disabled={!summary} className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-bold">PDF</Button>
            <Button data-testid="service-summary-csv-btn" onClick={() => { const s = summary; const rows = [["Champ", "Valeur"], ["Periode", `${fmtHM(s.window.start)} - ${s.window.closed ? fmtHM(s.window.end) : "en cours"}`], ["CA", s.revenue], ["Couverts", s.covers.total], ["Couverts_complet", s.covers.complete ? "oui" : "non"], ["Ticket_par_couvert", s.avg_ticket_per_cover ?? ""], ["Tables_servies", s.tables_served], ["Commandes", s.order_count], ["Upsells_nb", s.upsells.count], ["Upsells_valeur", s.upsells.value], ["Impayes", s.unpaid_amount]]; downloadBlob(new Blob([rows.map((r) => r.join(";")).join("\n")], { type: "text/csv;charset=utf-8" }), "resume-service.csv"); }} disabled={!summary} variant="outline" className="rounded-full bg-transparent border-[#1B3028] text-stone-200">CSV</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closeWarn} onOpenChange={(o) => !o && setCloseWarn(null)}>
        <DialogContent data-testid="service-close-dialog" className="max-w-md rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl flex items-center gap-2 text-amber-300"><AlertTriangle className="w-5 h-5" /> Opérations encore actives</DialogTitle>
            <DialogDescription className="text-stone-400">Le service contient encore des opérations en cours. Vérifiez avant de clôturer.</DialogDescription>
          </DialogHeader>
          {closeWarn && (
            <ul className="text-sm space-y-1.5">
              {closeWarn.open_table_count > 0 && <li className="text-stone-200">• {closeWarn.open_table_count} table(s) ouverte(s) : {closeWarn.open_tables?.join(", ")}</li>}
              {closeWarn.unfinished_orders > 0 && <li className="text-stone-200">• {closeWarn.unfinished_orders} commande(s) non terminée(s)</li>}
              {closeWarn.pending_requests > 0 && <li className="text-stone-200">• {closeWarn.pending_requests} appel(s) serveur en attente</li>}
              {closeWarn.amount_to_collect > 0 && <li className="text-[#E7C97A] font-semibold">• {eur(closeWarn.amount_to_collect)} encore à encaisser ({closeWarn.unpaid_tables?.join(", ")})</li>}
            </ul>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setCloseWarn(null)} className="rounded-full bg-transparent border-[#1B3028] text-stone-300">Continuer le service</Button>
            <Button data-testid="service-close-force" onClick={() => closeService(true)} className="rounded-full bg-red-500 hover:bg-red-600 text-white font-bold">Clôturer quand même</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


const PANEL_TITLE = {
  active_tables: "Détail des tables actives",
  orders_new: "Commandes en attente d'acceptation",
  orders_preparing: "Commandes en préparation",
  orders_ready: "Commandes prêtes à servir",
  orders_late: "Commandes en retard",
  waiter_requests: "Appels serveur",
  pending_payments: "Tables à encaisser",
  revenue: "Détail du CA du jour",
};

function Row({ children, testid, urgent }) {
  return <div data-testid={testid} className={`flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-sm ${urgent ? "bg-red-500/[0.06]" : ""}`}>{children}</div>;
}
const Lbl = ({ children }) => <span className="text-stone-500 text-xs">{children}</span>;
const Val = ({ children, className = "" }) => <span className={`text-stone-200 ${className}`}>{children}</span>;
const NavLink = ({ onClick, children }) => (
  <button onClick={onClick} className="ml-auto text-[11px] font-semibold text-[#C69C4A] hover:underline inline-flex items-center gap-1">{children} <ArrowRight className="w-3 h-3" /></button>
);

function CockpitDetail({ metric, detail, onClose, onNavigate, caRevenue }) {
  const { loading, data } = detail;
  const rows = data?.rows || [];
  return (
    <div data-testid="cockpit-detail-panel" className="rounded-2xl bg-[#0C1713] border border-[#1B3028] overflow-hidden animate-slide-up">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#1B3028]">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#E7C97A]">{PANEL_TITLE[metric]}{data ? ` · ${data.count}` : ""}</h3>
        <button data-testid="cockpit-detail-close" onClick={onClose} className="text-stone-500 hover:text-stone-100"><X className="w-4 h-4" /></button>
      </div>
      {loading && !data ? (
        <p className="px-4 py-8 text-center text-stone-500 text-sm">Chargement…</p>
      ) : !data ? (
        <p data-testid="cockpit-detail-unavailable" className="px-4 py-8 text-center text-stone-500 text-sm">Information non disponible</p>
      ) : rows.length === 0 ? (
        <p data-testid="cockpit-detail-empty" className="px-4 py-8 text-center text-stone-500 text-sm">Aucun élément.</p>
      ) : (
        <div className="divide-y divide-[#132420] max-h-[360px] overflow-y-auto">
          {metric === "active_tables" && rows.map((r) => (
            <Row key={r.table_number} testid="cockpit-row">
              <Val className="font-semibold w-20">Table {r.table_number}</Val>
              <Val className="text-emerald-400 text-xs">{r.status}</Val>
              <span><Lbl>ouv. </Lbl><Val>{fmtHM(r.opened_at)}</Val></span>
              <span><Lbl>durée </Lbl><Val>{fmtDur(r.duration_seconds)}</Val></span>
              <span><Lbl>cmd </Lbl><Val>{r.order_count}</Val></span>
              <span><Lbl>total </Lbl><Val>{eur(r.total)}</Val></span>
              <span><Lbl>payé </Lbl><Val>{eur(r.paid_amount)}</Val></span>
              <span><Lbl>reste </Lbl><Val className={r.remaining > 0 ? "text-amber-400 font-semibold" : ""}>{eur(r.remaining)}</Val></span>
              <NavLink onClick={() => onNavigate("tables")}>Voir la table</NavLink>
            </Row>
          ))}
          {metric === "orders_new" && rows.map((r, i) => (
            <Row key={i} testid="cockpit-row">
              <Val className="font-mono text-xs w-28">{r.order_code}</Val>
              <Val className="w-16">T. {r.table_number}</Val>
              <span><Lbl>{fmtHM(r.created_at)}</Lbl></span>
              <span><Lbl>attente </Lbl><Val className="text-amber-400">{fmtDur(r.wait_seconds)}</Val></span>
              <span><Lbl>{r.items_count} art.</Lbl></span>
              <Val className="font-semibold">{eur(r.total)}</Val>
              <NavLink onClick={() => onNavigate("orders")}>Voir la commande</NavLink>
            </Row>
          ))}
          {metric === "orders_preparing" && rows.map((r, i) => (
            <Row key={i} testid="cockpit-row" urgent={r.late}>
              <Val className="font-mono text-xs w-28">{r.order_code}</Val>
              <Val className="w-16">T. {r.table_number}</Val>
              <span><Lbl>accepté </Lbl><Val>{fmtHM(r.accepted_at)}</Val></span>
              <span><Lbl>début </Lbl><Val>{r.started_at ? fmtHM(r.started_at) : "—"}</Val></span>
              <span><Lbl>prépa </Lbl><Val className={r.late ? "text-red-400 font-semibold" : ""}>{fmtDur(r.prep_seconds)}</Val></span>
              {r.late && <span className="text-[10px] font-bold uppercase text-red-400 bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5">Retard</span>}
              <NavLink onClick={() => onNavigate("orders")}>Voir la commande</NavLink>
            </Row>
          ))}
          {metric === "orders_ready" && rows.map((r, i) => (
            <Row key={i} testid="cockpit-row" urgent={(r.wait_since_ready_seconds || 0) > 300}>
              <Val className="font-mono text-xs w-28">{r.order_code}</Val>
              <Val className="w-16">T. {r.table_number}</Val>
              <span><Lbl>prête à </Lbl><Val>{r.ready_at ? fmtHM(r.ready_at) : "—"}</Val></span>
              <span><Lbl>en attente depuis </Lbl><Val className={(r.wait_since_ready_seconds || 0) > 300 ? "text-red-400 font-semibold" : ""}>{fmtDur(r.wait_since_ready_seconds)}</Val></span>
              <NavLink onClick={() => onNavigate("orders")}>Voir la commande</NavLink>
            </Row>
          ))}
          {metric === "orders_late" && rows.map((r, i) => (
            <Row key={i} testid="cockpit-row" urgent>
              <Val className="font-mono text-xs w-28">{r.order_code}</Val>
              <Val className="w-16">T. {r.table_number}</Val>
              <span><Lbl>étape </Lbl><Val>{r.step}</Val></span>
              <span><Lbl>écoulé </Lbl><Val>{fmtDur(r.elapsed_seconds)}</Val></span>
              <span><Lbl>seuil </Lbl><Val>{fmtDur(r.threshold_seconds)}</Val></span>
              <span><Lbl>retard </Lbl><Val className="text-red-400 font-semibold">+{fmtDur(r.delay_seconds)}</Val></span>
              <NavLink onClick={() => onNavigate("orders")}>Voir la commande</NavLink>
            </Row>
          ))}
          {metric === "waiter_requests" && rows.map((r) => (
            <Row key={r.id} testid="cockpit-row" urgent={r.urgent}>
              <Val className="font-semibold w-16">T. {r.table_number}</Val>
              <Val>{r.label || r.type}</Val>
              <span><Lbl>{fmtHM(r.created_at)}</Lbl></span>
              <span><Lbl>attente </Lbl><Val className={r.urgent ? "text-red-400 font-semibold" : ""}>{fmtDur(r.wait_seconds)}</Val></span>
              <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${r.status === "Prise en charge" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-[#C69C4A]/10 text-[#C69C4A] border border-[#C69C4A]/30"}`}>{r.status}</span>
              {r.claimed_by && <span className="text-[11px] text-emerald-400">· {r.claimed_by}</span>}
            </Row>
          ))}
          {metric === "pending_payments" && rows.map((r) => (
            <Row key={r.table_number} testid="cockpit-row">
              <Val className="font-semibold w-20">Table {r.table_number}</Val>
              <span><Lbl>total </Lbl><Val>{eur(r.bill_total)}</Val></span>
              <span><Lbl>payé </Lbl><Val>{eur(r.paid_amount)}</Val></span>
              <span><Lbl>reste </Lbl><Val className="text-amber-400 font-semibold">{eur(r.remaining)}</Val></span>
              <NavLink onClick={() => onNavigate("payments")}>Voir le paiement</NavLink>
            </Row>
          ))}
          {metric === "revenue" && rows.map((r, i) => (
            <Row key={i} testid="cockpit-row">
              <span className="w-16"><Lbl>{fmtHM(r.created_at)}</Lbl></span>
              <Val className="font-mono text-xs w-28">{r.order_code}</Val>
              <Val className="w-16">T. {r.table_number}</Val>
              <span className="text-[11px] text-stone-500">{r.source === "CLIENT_QR" ? "QR" : "POS"}</span>
              <span className="text-[11px] text-stone-500">{r.status}</span>
              <Val className="ml-auto font-semibold">{eur(r.total)}</Val>
            </Row>
          ))}
        </div>
      )}
      {data && (metric === "revenue" || metric === "pending_payments") && (
        <div data-testid="cockpit-detail-total" className="flex items-center justify-between px-4 py-3 border-t border-[#1B3028] bg-[#111F1A]">
          <span className="text-xs uppercase tracking-wider text-stone-400">Total</span>
          <span className="font-bold text-lg text-[#E7C97A]">{eur(metric === "revenue" ? (data.total ?? caRevenue) : data.total_remaining)}</span>
        </div>
      )}
    </div>
  );
}
